import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../../lib/utils";
import {
  Play,
  LayoutDashboard,
  GitBranch,
  Edit,
  Plus,
  Check,
  Settings,
} from "lucide-react";
import { Input } from "./input";
import { Button } from "./button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { Badge } from "./badge";

/**
 * MainNavigation component
 *
 * Provides consistent navigation across the application with the main sections:
 * - Dashboard
 * - Variables
 * - Bulk Run Pipelines
 * - Pipeline Status
 */
const MainNavigation = ({ className }) => {
  const location = useLocation();
  // Get default branch from environment variable or localStorage
  const ENV_DEFAULT_BRANCH = process.env.REACT_APP_DEFAULT_BRANCH || "";

  const [defaultBranch, setDefaultBranch] = useState(
    localStorage.getItem("defaultBranch") || ENV_DEFAULT_BRANCH
  );
  const [showBranchInput, setShowBranchInput] = useState(false);
  const [inputValue, setInputValue] = useState(defaultBranch);
  const inputRef = useRef(null);

  // Define the navigation items
  const navItems = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: <LayoutDashboard className="h-4 w-4 mr-2" />,
    },
    {
      name: "Bulk Run Pipelines",
      href: "/pipelines",
      icon: <Play className="h-4 w-4 mr-2" />,
    },
    {
      name: "Pipeline Status",
      href: "/pipelines-status",
      // Match any path starting with /pipelines-status
      active: location.pathname.startsWith("/pipelines-status"),
      icon: <GitBranch className="h-4 w-4 mr-2" />,
    },
    {
      name: "Settings",
      href: "/settings",
      icon: <Settings className="h-4 w-4 mr-2" />,
    },
  ];

  // Save defaultBranch to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("defaultBranch", defaultBranch);
  }, [defaultBranch]);

  // Focus input when editing starts
  useEffect(() => {
    if (showBranchInput && inputRef.current) {
      inputRef.current.focus();
      // Set input value to current branch when starting to edit
      setInputValue(defaultBranch);
    }
  }, [showBranchInput, defaultBranch]);

  const handleBranchChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleBranchToggle = () => {
    setShowBranchInput(!showBranchInput);
  };

  const handleSaveBranch = () => {
    setDefaultBranch(inputValue);
    setShowBranchInput(false);
  };

  const handleBranchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveBranch();
    } else if (e.key === "Escape") {
      setShowBranchInput(false);
      setInputValue(defaultBranch); // Reset to original value
    }
  };

  return (
    <nav
      className={cn(
        "flex items-center space-x-4 lg:space-x-6 px-4 py-3 bg-white dark:bg-gray-900 border-b mb-6",
        className
      )}
    >
      <h1 className="text-xl font-bold mr-8">Bitbucket Manager</h1>

      {navItems.map((item) => {
        // Check if the current location matches this nav item
        const isActive =
          item.active !== undefined
            ? item.active
            : location.pathname === item.href;

        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={item.onClick}
            className={cn(
              "flex items-center text-sm font-medium transition-colors hover:text-primary",
              isActive
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {item.icon}
            {item.name}
          </Link>
        );
      })}

      <div className="ml-auto flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {defaultBranch ? (
                <Badge
                  variant="outline"
                  className={cn(
                    "flex items-center gap-1 px-3 py-1",
                    !showBranchInput && "cursor-pointer hover:bg-muted"
                  )}
                  onClick={!showBranchInput ? handleBranchToggle : undefined}
                >
                  <GitBranch className="h-3 w-3 flex-shrink-0" />

                  {showBranchInput ? (
                    <div className="flex items-center">
                      <Input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleBranchChange}
                        onKeyDown={handleBranchKeyDown}
                        className="h-5 text-xs min-w-28 border-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-5 w-5 ml-1"
                        onClick={handleSaveBranch}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="text-xs font-normal">
                        {defaultBranch}
                      </span>
                      <Edit className="h-3 w-3 ml-1 opacity-50" />
                    </>
                  )}
                </Badge>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBranchToggle}
                  className="h-7 text-xs px-2"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Set Default Branch
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {defaultBranch
                  ? "Click to change default branch"
                  : "Set default branch for pipelines"}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </nav>
  );
};

export { MainNavigation };
